import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@hotjar/navigation';
import { tokens, Loader, Text, Button, Stack } from '@hotjar/hotsauce';
import { SidebarCategory } from '@hotjar/insights-common/components/SidebarCategory';
import { useActivityFeed } from '@hotjar/webapp/hooks/useActivityFeed';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { ActivityFeedItem, TrackingCodeFeedItem } from './ActivityFeedItem';

const Feed = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const FeedItem = styled.li`
  margin: 0;
  padding: ${tokens.spacer.x2} 0 ${tokens.spacer.x1};
  ${tokens.text('caption')}

  &:first-of-type {
    padding-top: 0;
  }

  &:last-of-type {
    padding-bottom: ${tokens.spacer.x2};
  }
`;

const LoaderWrapper = styled.div`
  padding: ${tokens.spacer.x2};
`;

const ScrollableSidebarCategory = styled(SidebarCategory)`
  overflow: auto;
`;

interface ActivityFeedProps {
  trackingUIPosition?: string;
}

export function ActivityFeed({ trackingUIPosition }: ActivityFeedProps): JSX.Element {
  const { t } = useTranslation();
  const track = useTrack();
  const { routes, buildHrefFromRoute } = useNavigation();
  const canInvite = usePermission('org-invite');
  const { data: feedEvents, isLoading, isError } = useActivityFeed();

  const handleOnInviteClick = () => {
    track.click('Invite team member', trackingUIPosition || 'Activity Feed', {});
  };

  const renderCardBody = () => {
    return isError ? (
      <div>{null}</div>
    ) : (
      <Feed data-testid="card-content">
        {feedEvents?.length ? (
          feedEvents?.map((feedEvent) => (
            <FeedItem key={feedEvent.id} data-testid="card-activity-feed-item">
              {feedEvent.action === 'script_added' ? (
                <TrackingCodeFeedItem trackingUIPosition={trackingUIPosition} />
              ) : (
                <ActivityFeedItem trackingUIPosition={trackingUIPosition} {...feedEvent} />
              )}
            </FeedItem>
          ))
        ) : (
          <>
            {canInvite ? (
              <FeedItem style={{ paddingLeft: '22px' }}>
                <Stack direction="column" spacing="x1">
                  <Text textColor="text_light" textType="body2">
                    {t('activation:dashboard.activity-feed-card.invite-team-member')}
                  </Text>
                  <div>
                    <Button
                      onClick={handleOnInviteClick}
                      as="a"
                      href={buildHrefFromRoute(routes.TEAM.INVITE.path)}
                      size="s"
                      kind="secondary"
                    >
                      {t('activation:dashboard.activity-feed-card.invite-team-member-cta')}
                    </Button>
                  </div>
                </Stack>
              </FeedItem>
            ) : (
              <Text textColor="text_light" textType="body2">
                {t('activation:dashboard.activity-feed-card.spaces-no-activity')}
              </Text>
            )}
          </>
        )}
      </Feed>
    );
  };

  return (
    <ScrollableSidebarCategory
      hideDivider
      categoryId="activity-feed"
      title={t('activation:dashboard.activity-feed-card.recent-team-activity')}
    >
      {isLoading ? (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      ) : (
        renderCardBody()
      )}
    </ScrollableSidebarCategory>
  );
}
